import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { membersApi } from '~/entities/channel/api/members'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'

type MembersTarget = { businessId: string; channelId: string }

const membersKey = (channelId: string) => queryKey(QueryKey.ORGANIZER_MEMBERS, channelId)

export function useOrganizerMembers(target: MembersTarget | null, q: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.ORGANIZER_MEMBERS, target?.channelId ?? null, q),
    queryFn: () => membersApi.list(target as MembersTarget, q || undefined),
    enabled: !!target,
    placeholderData: keepPreviousData,
  })
}

function useMembersMutation<TInput, TResult>(
  target: MembersTarget | null,
  run: (target: MembersTarget, input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TInput) => run(target as MembersTarget, input),
    onSettled: () => {
      if (!target) return
      queryClient.invalidateQueries({ queryKey: membersKey(target.channelId) })
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.ORGANIZER_PROFILE, target.businessId),
      })
    },
  })
}

export function useSetMembersEnabled(target: MembersTarget | null) {
  return useMembersMutation(target, (t, enabled: boolean) => membersApi.setEnabled(t, enabled))
}

export function useRotateMemberInvite(target: MembersTarget | null) {
  return useMembersMutation(target, (t, _: undefined) => membersApi.rotateInvite(t))
}

export function useApproveMember(target: MembersTarget | null) {
  return useMembersMutation(target, (t, memberId: string) => membersApi.approve(t, memberId))
}

export function useApproveAllMembers(target: MembersTarget | null) {
  return useMembersMutation(target, (t, _: undefined) => membersApi.approveAll(t))
}

export function useDeclineMember(target: MembersTarget | null) {
  return useMembersMutation(target, (t, memberId: string) => membersApi.decline(t, memberId))
}

export function useRemoveMember(target: MembersTarget | null) {
  return useMembersMutation(target, (t, memberId: string) => membersApi.remove(t, memberId))
}
